import React, { useCallback, useRef } from "react";
import { GeolocateControl as MapboxGeolocateControl } from "mapbox-gl";
import { useTranslation } from "react-i18next";
import { GeolocateControl, useMap } from "react-map-gl/mapbox-legacy";
import { MapboxButtonMimic } from "./MapboxButtonMimic";

// Workaround for https://github.com/mapbox/mapbox-gl-js/issues/12073
// _setupUI fires asynchronously and crashes if the control was already removed.
// React 18 StrictMode double-mounts components, and GeolocateControl doesn't
// handle this gracefully — it tries to access _map after the control is removed.
// Fixed in v3 (https://github.com/mapbox/mapbox-gl-js/pull/12332) but not
// backported to v2 (last MIT-licensed release).
// Must be a prototype-level patch so it's applied before mapbox-gl binds _setupUI.
const proto = MapboxGeolocateControl.prototype as unknown as {
  _setupUI: (args: unknown) => void;
};
const originalSetupUI = proto._setupUI;
proto._setupUI = function (this: { _map: unknown }, args: unknown) {
  if (this._map) {
    originalSetupUI.call(this, args);
  }
};

export const GeolocationTool = ({
  geolocateClicked,
}: {
  geolocateClicked: () => Promise<void>;
}) => {
  const { t } = useTranslation();
  const { current: map } = useMap();
  const geolocateControlRef = useRef<MapboxGeolocateControl>(null);

  const handleClick = useCallback(async () => {
    await geolocateClicked();
    geolocateControlRef.current?.trigger();
  }, [geolocateClicked]);

  return (
    <>
      {/* Hidden GeolocateControl provides access to the trigger() method for
          activating browser geolocation. We render our own button via
          MapboxButtonMimic to control positioning. */}
      {map && (
        <GeolocateControl
          ref={geolocateControlRef}
          trackUserLocation
          fitBoundsOptions={{ maxZoom: 18, minZoom: 18 }}
          position="bottom-right"
          style={{ display: "none" }}
        />
      )}
      <MapboxButtonMimic
        showLabel
        buttons={[
          {
            iconClass: "mapboxgl-ctrl-geolocate",
            onClick: handleClick,
            label: t("button-show-location"),
          },
        ]}
      />
    </>
  );
};
